import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Link2, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const Delivery = ({ d }) => (
  <div className={`flex items-start gap-2 text-xs ${d.result === "enviado" ? "text-emerald-300" : "text-amber-300"}`} data-testid={`email-delivery-${d.id}`}>
    {d.result === "enviado" ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
    <span>
      {d.result === "enviado" ? (d.delivery === "enlace" ? (d.kind === "reenvio" ? "Enlace al PDF reenviado al cliente" : "Enlace al PDF enviado al cliente") : (d.kind === "reenvio" ? "PDF reenviado al cliente" : "PDF enviado al cliente")) : "Error al enviar el correo"}
      {" · "}{fmtDateTime(d.at)} · <span className="font-mono">{d.to}</span> · {d.user_name}
      {d.error && <span className="block text-muted-foreground">{d.error}</span>}
    </span>
  </div>
);

const Field = ({ label, children }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 text-sm">{children}</div></div>
);

const SendDialog = ({ info, open, onOpenChange, onSend, busy }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-xl" data-testid="send-client-dialog">
      <DialogHeader><DialogTitle className="font-display">Enviar PDF al cliente</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <Field label="Destinatario"><span className="font-mono" data-testid="send-client-to">{info.to}</span></Field>
        <Field label="Asunto"><span data-testid="send-client-subject">{info.subject}</span></Field>
        <Field label="Mensaje"><p className="whitespace-pre-wrap rounded-lg border border-border bg-card/60 p-3 text-sm" data-testid="send-client-message">{info.message}</p></Field>
        <Field label="PDF"><span className="inline-flex items-center gap-1.5 font-mono" data-testid="send-client-attachment"><Link2 className="h-4 w-4 text-primary" />Enlace seguro de descarga a {info.attachment} (PDF definitivo, vigente {info.link_days} días)</span></Field>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="send-client-cancel-button">Cancelar</Button>
        <Button onClick={onSend} disabled={busy} data-testid="send-client-confirm-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}Enviar</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ClientEmailPanel = ({ woId }) => {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/work-orders/${woId}/client-email`).then((r) => setInfo(r.data)), [woId]);
  useEffect(() => { load(); }, [load]);
  if (!info) return null;
  const last = info.deliveries[0];
  const sent = info.deliveries.some((d) => d.result === "enviado");
  const label = last?.result === "error" ? "Reintentar envío" : sent ? "Reenviar al cliente" : "Enviar al cliente";

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/work-orders/${woId}/send-to-client`, { base_url: window.location.origin });
      if (data.result === "enviado") { toast.success(`Enlace al PDF enviado a ${data.to}`); setOpen(false); }
      else toast.error(`Error al enviar el correo: ${data.error}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); load(); }
  };

  return (
    <div className="mb-5 space-y-2 rounded-lg border border-border bg-card/40 px-4 py-3" data-testid="client-email-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Mail className="h-4 w-4 text-primary" />
        <span className="text-sm">Correo del cliente (snapshot): <span className="font-mono" data-testid="client-email-to">{info.to || "—"}</span></span>
        <Button size="sm" className="ml-auto" disabled={!info.can_send} onClick={() => setOpen(true)} data-testid="send-client-button"><Mail className="mr-2 h-4 w-4" />{label}</Button>
      </div>
      {!info.to && <p className="flex items-center gap-2 text-xs text-amber-400" data-testid="client-email-missing"><AlertTriangle className="h-3.5 w-3.5" />Esta OS no tiene un correo de cliente registrado.</p>}
      {info.deliveries.length > 0 && <div className="space-y-1.5 border-t border-border pt-2" data-testid="email-delivery-history">{info.deliveries.map((d) => <Delivery key={d.id} d={d} />)}</div>}
      <SendDialog info={info} open={open} onOpenChange={setOpen} onSend={send} busy={busy} />
    </div>
  );
};
